import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  MixedLineBarChart,
  Select,
  SelectProps,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  getCampaignStats,
  getCampaignTimeseries,
  getCampaignTopUrls,
  listDripCampaigns,
} from '../../../../common/api/dripCampaigns';
import { MetricComparison } from '../../../../components/stats/metrics';
import { DashboardViewLayout } from '../../../../components/DashboardViewLayout';

const DAYS_OPTIONS: SelectProps.Options = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 60 days', value: '60' },
  { label: 'Last 90 days', value: '90' },
];

export default function DripCampaignDetailPage() {
  const { uuid } = useParams({ from: '/dashboard/drip/campaigns_/$uuid' });
  const navigate = useNavigate();
  const [days, setDays] = useState<SelectProps.Option>({ label: 'Last 30 days', value: '30' });
  const daysNumber = days.value ? parseInt(days.value) : 30;

  const campaignList = useQuery({
    queryKey: ['drip-campaigns', 30],
    queryFn: () => listDripCampaigns(30),
  });
  const campaign = campaignList.data?.find((c) => c.uuid === uuid);

  const stats = useQuery({
    queryKey: ['campaign-stats', uuid, daysNumber],
    queryFn: () => getCampaignStats(uuid, daysNumber),
    refetchInterval: 30_000,
  });
  const series = useQuery({
    queryKey: ['campaign-timeseries', uuid, daysNumber],
    queryFn: () => getCampaignTimeseries(uuid, daysNumber),
    refetchInterval: 30_000,
  });
  const topUrls = useQuery({
    queryKey: ['campaign-top-urls', uuid, daysNumber],
    queryFn: () => getCampaignTopUrls(uuid, daysNumber, 20),
    refetchInterval: 60_000,
  });

  const current = stats.data?.current;
  const prior = stats.data?.prior;

  const points = series.data?.points ?? [];
  const sentSeries = points.map((p) => ({ x: new Date(p.date), y: p.sent }));
  const openedSeries = points.map((p) => ({ x: new Date(p.date), y: p.opened }));
  const clickedSeries = points.map((p) => ({ x: new Date(p.date), y: p.clicked }));
  const bouncedSeries = points.map((p) => ({ x: new Date(p.date), y: p.bounced }));

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Drip', href: '/dashboard/drip/dashboard' },
        { text: 'Campaigns', href: '/dashboard/drip/campaigns' },
        { text: campaign?.name ?? uuid, href: `/dashboard/drip/campaigns/${uuid}` },
      ]}
      header={
        <Header
          variant="h1"
          description={
            campaign
              ? `${campaign.name} · template ${campaign.current_template_version ?? '—'}`
              : 'Loading campaign…'
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                onClick={() =>
                  navigate({
                    to: '/dashboard/drip/contacts',
                    search: { campaign_uuid: uuid },
                  })
                }
              >
                View all campaign contacts
              </Button>
              <Button
                onClick={() =>
                  navigate({
                    to: '/dashboard/drip/emails',
                    search: { campaign_uuid: uuid },
                  })
                }
              >
                View all campaign emails
              </Button>
              <Select
                selectedOption={days}
                onChange={(e) => e.detail.selectedOption && setDays(e.detail.selectedOption)}
                options={DAYS_OPTIONS}
              />
            </SpaceBetween>
          }
        >
          Campaign overview
        </Header>
      }
    >
      <SpaceBetween size="l">
        {stats.error || series.error || topUrls.error ? (
          <Alert type="error" header="Failed to load campaign data">
            {String(stats.error ?? series.error ?? topUrls.error)}
          </Alert>
        ) : null}

        <Container header={<Header variant="h2">Top-line</Header>}>
          <ColumnLayout columns={6} variant="text-grid">
            <MetricComparison
              header="Scheduled"
              value={current?.scheduled}
              previousValue={prior?.scheduled}
              previousInfo={`Prior ${daysNumber} days`}
            />
            <MetricComparison
              header="Sent"
              value={current?.sent}
              previousValue={prior?.sent}
              previousInfo={`Prior ${daysNumber} days`}
            />
            <MetricComparison
              header="Delivered"
              value={current?.delivered}
              previousValue={prior?.delivered}
              previousInfo={`Prior ${daysNumber} days`}
            />
            <MetricComparison
              header="Opened"
              value={current?.opened}
              previousValue={prior?.opened}
              previousInfo={`Prior ${daysNumber} days`}
            />
            <MetricComparison
              header="Clicked"
              value={current?.clicked}
              previousValue={prior?.clicked}
              previousInfo={`Prior ${daysNumber} days`}
            />
            <MetricComparison
              header="Bounced / Unsub"
              value={(current?.bounced ?? 0) + (current?.unsubscribed ?? 0)}
              previousValue={(prior?.bounced ?? 0) + (prior?.unsubscribed ?? 0)}
              previousInfo={`Prior ${daysNumber} days`}
              inverted
            />
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h2">Daily activity</Header>}>
          <MixedLineBarChart
            statusType={series.isLoading ? 'loading' : 'finished'}
            height={300}
            series={[
              { type: 'bar', title: 'Sent', data: sentSeries },
              { type: 'line', title: 'Opened', data: openedSeries },
              { type: 'line', title: 'Clicked', data: clickedSeries },
              { type: 'line', title: 'Bounced', data: bouncedSeries },
            ]}
            xScaleType="time"
            xTitle="Day"
            yTitle="Count"
            empty={
              <Box textAlign="center" color="inherit">
                <b>No activity in the last {daysNumber} days.</b>
              </Box>
            }
          />
        </Container>

        <Container header={<Header variant="h2">Top clicked URLs</Header>}>
          <Table
            variant="embedded"
            loading={topUrls.isLoading}
            loadingText="Loading top URLs…"
            items={topUrls.data?.urls ?? []}
            empty={
              <Box textAlign="center">
                <b>No clicks recorded in the last {daysNumber} days.</b>
              </Box>
            }
            columnDefinitions={[
              {
                id: 'url',
                header: 'URL',
                cell: (i) => (
                  <Link href={i.url} external>
                    {i.url}
                  </Link>
                ),
              },
              {
                id: 'clicks',
                header: 'Clicks',
                cell: (i) => i.click_count.toLocaleString(),
                width: 100,
              },
              {
                id: 'unique',
                header: 'Unique recipients',
                cell: (i) => i.unique_email_count.toLocaleString(),
                width: 160,
              },
              {
                id: 'ignored',
                header: 'Ignored',
                cell: (i) => i.ignored_count.toLocaleString(),
                width: 90,
              },
            ]}
          />
        </Container>
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
